import React, { useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import SupportChat from "./common/SupportChat";

// EN/中文 toggle. Only Main.js and MyFiles.jsx have zh translations; other
// pages stay English either way. Default: the user's saved choice, else the
// browser language (any zh-* → 中文), else English.
function initialLang() {
  try {
    const saved = localStorage.getItem("lang");
    if (saved === "en" || saved === "zh") return saved;
  } catch {}
  return (navigator.language || "").toLowerCase().startsWith("zh") ? "zh" : "en";
}

export default function Layout({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [supportOpen, setSupportOpen] = useState(false);
  const [lang, setLang] = useState(initialLang);

  // Only persisted once the user actually picks one, so the browser-language
  // default keeps applying until then.
  const chooseLang = (next) => {
    setLang(next);
    try { localStorage.setItem("lang", next); } catch {}
  };

  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang]);

  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;

  const handleLogout = () => {
    onLogout(); // ✅ now defined
    navigate("/login", { replace: true });
  };


  // Routes that should be centered
  const centeredRoutes = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/",
  ];

  const centerContent = centeredRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  const isMyFiles = location.pathname.startsWith("/myfiles");

  return (
    <div className={`bg-dark-bg text-white flex flex-col ${isMyFiles ? "h-screen overflow-hidden" : "min-h-screen"}`}>
      {/* ---------- Header ---------- */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-dark-border bg-dark-bg">
        <div
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-2xl font-bold text-primary cursor-pointer hover:opacity-80 transition"
        >
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-dark-bg font-bold">⛓️</span>
          </div>
          <span>ShadowVault</span>
        </div>

        <nav className="hidden md:flex items-center gap-3">
          {!isMyFiles && (
            <>
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-10 border border-primary border-opacity-30 rounded-full px-4 py-2 text-sm text-primary font-medium">
                <span>⛓️</span> Blockchain-Secured Legal Documents
              </div>
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-10 border border-primary border-opacity-30 rounded-full px-4 py-2 text-sm text-primary font-medium">
                <span>🧠</span> Run by AI Brain — Monitored 24/7
              </div>
            </>
          )}
        </nav>

        <div className="flex items-center gap-4">
          {/* Language selector */}
          <div className="inline-flex items-center bg-dark-card border border-dark-border rounded-full p-1 text-sm">
            <button
              onClick={() => chooseLang("en")}
              className={`px-3 py-1 rounded-full font-medium transition ${
                lang === "en" ? "bg-primary text-dark-bg" : "text-gray-400 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => chooseLang("zh")}
              className={`px-3 py-1 rounded-full font-medium transition ${
                lang === "zh" ? "bg-primary text-dark-bg" : "text-gray-400 hover:text-white"
              }`}
            >
              中文
            </button>
          </div>

          {/* ✅ Auth-aware button */}
          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="px-5 py-2 text-sm font-medium bg-primary text-dark-bg hover:bg-primary-dark rounded-lg transition font-semibold"
            >
              Logout
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="px-5 py-2 text-sm font-medium bg-primary text-dark-bg hover:bg-primary-dark rounded-lg transition font-semibold"
            >
              Get Started
            </button>
          )}
        </div>
      </header>

      {/* ---------- Main ---------- */}
      <main
        className={`flex flex-1 ${
          isMyFiles
            ? "overflow-hidden"
            : centerContent
            ? "px-6 items-center justify-center overflow-y-auto"
            : "overflow-y-auto"
        }`}
      >
        <Outlet context={{ lang, setLang: chooseLang }} />
      </main>

      {/* ---------- Footer ---------- */}
      <footer className="py-8 border-t border-dark-border bg-dark-bg">
        <div className="max-w-6xl mx-auto px-8 grid grid-cols-2 md:grid-cols-3 gap-4 text-center md:text-left text-sm text-gray-400 mb-6">
          <div className="flex items-center justify-center md:justify-start gap-2"><span>🔐</span> AES-256 Encryption</div>
          <div className="flex items-center justify-center md:justify-start gap-2"><span>🌐</span> Data Privacy</div>
          <button
            onClick={() => setSupportOpen(true)}
            className="flex items-center justify-center md:justify-start gap-2 hover:text-primary transition cursor-pointer"
          >
            <span>📞</span> 24/7 Support
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-8 flex items-center justify-center gap-4 text-xs text-gray-500 mb-4">
          <Link to="/terms" className="hover:text-primary transition">Terms of Service</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-primary transition">Privacy Policy</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-primary transition">{lang === "zh" ? "联系我们" : "Contact Us"}</Link>
        </div>
        <div className="text-center text-gray-500 text-xs border-t border-dark-border pt-6">
          ShadowVault is a product of AfterLight Technologies Inc. © {new Date().getFullYear()} All rights reserved.
        </div>
      </footer>

      {/* Unmounted on close (not just hidden) so reopening starts a fresh conversation */}
      {supportOpen && <SupportChat onClose={() => setSupportOpen(false)} />}
    </div>
  );
}
